import React, { useState, useRef, useEffect } from "react";
import { Flame, Clock, ChevronDown, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PRESET_DELAYS = [
  { label: "In 15 seconds", seconds: 15 },
  { label: "In 30 seconds", seconds: 30 },
  { label: "In 1 minute",   seconds: 60 },
  { label: "In 2 minutes",  seconds: 120 },
  { label: "In 5 minutes",  seconds: 300 },
];

/**
 * Admin-only split button. Left side fires now; right side opens a scheduler menu.
 * Cooldown of 10s on instant boost to prevent double-taps.
 */
export default function CrewBoostButton({ streamId, compact = false }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [lastFired, setLastFired] = useState(0);
  const [menu, setMenu] = useState(false);
  const [customSecs, setCustomSecs] = useState(90);
  const menuRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!user || user.role !== "admin") return null;

  const fireNow = async () => {
    if (Date.now() - lastFired < 10000) {
      toast.info(`Cooldown: ${Math.ceil((10000 - (Date.now() - lastFired)) / 1000)}s`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/crew-boost/${streamId}`, { count: 20, duration_seconds: 6 });
      toast.success(`🔥 ${data.count}-member crew boost incoming`);
      setLastFired(Date.now());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const schedule = async (seconds) => {
    setBusy(true);
    try {
      await api.post(`/admin/crew-boost/${streamId}/schedule`, { delay_seconds: seconds, count: 20, duration_seconds: 6 });
      const label = seconds < 60 ? `${seconds}s` : `${Math.round(seconds / 60)} min`;
      toast.success(`Boost scheduled to fire in ${label}`);
      setMenu(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const sizing = compact ? "text-[11px]" : "text-xs";
  const padding = compact ? "py-1" : "py-2";

  return (
    <div className="relative inline-flex" ref={menuRef} data-testid="crew-boost-container">
      <button
        onClick={fireNow}
        disabled={busy}
        data-testid="crew-boost-btn"
        className={`flex items-center gap-1.5 ${sizing} ${padding} px-3 rounded-l-full font-bold text-white bg-gradient-to-r from-tv-live via-orange-500 to-tv-accent hover:brightness-110 disabled:opacity-60 shadow-lg shadow-tv-live/30 transition`}
        title="Fire crew boost now"
      >
        <Flame className={`${compact ? "w-3 h-3" : "w-3.5 h-3.5"}`} />
        {busy ? "…" : "Crew Boost"}
      </button>
      <button
        onClick={() => setMenu((m) => !m)}
        data-testid="crew-boost-schedule-toggle"
        className={`flex items-center ${sizing} ${padding} px-1.5 rounded-r-full font-bold text-white bg-gradient-to-br from-tv-accent to-orange-500 hover:brightness-110 border-l border-white/25`}
        title="Schedule a boost"
      >
        <ChevronDown className={`${compact ? "w-3 h-3" : "w-3.5 h-3.5"}`} />
      </button>

      {menu && (
        <div data-testid="crew-boost-menu" className="absolute right-0 top-full mt-2 w-60 bg-[#0B1120] border border-white/15 rounded-xl shadow-2xl overflow-hidden z-50">
          <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-tv-accent font-bold flex items-center gap-1 border-b border-white/10">
            <Clock className="w-3 h-3" /> Schedule boost
          </div>
          {PRESET_DELAYS.map((d) => (
            <button
              key={d.seconds}
              onClick={() => schedule(d.seconds)}
              disabled={busy}
              data-testid={`crew-boost-preset-${d.seconds}`}
              className="w-full text-left px-4 py-2 text-sm text-white/85 hover:bg-white/5 hover:text-white transition"
            >
              {d.label}
            </button>
          ))}
          <form
            onSubmit={(e) => { e.preventDefault(); schedule(Math.max(5, parseInt(customSecs || 5, 10))); }}
            className="px-4 py-2 border-t border-white/10 flex items-center gap-1.5"
          >
            <input
              type="number"
              min={5}
              max={3600}
              value={customSecs}
              onChange={(e) => setCustomSecs(e.target.value)}
              data-testid="crew-boost-custom-secs"
              className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-sm text-white outline-none"
            />
            <span className="text-[11px] text-white/50">sec</span>
            <button disabled={busy} data-testid="crew-boost-custom-submit" className="bg-tv-accent text-black text-[11px] font-bold px-2 py-1 rounded">Go</button>
          </form>
        </div>
      )}
    </div>
  );
}
